import { ClarionCallout } from '@clarion/ClarionCallout';
import { useUserState } from '../auth/UserState';
import { useWorkspaces } from '../auth/WorkspaceState';
import { SHOW_PROD_WORKSPACE_MAINTENANCE_BANNER } from '../config/prodWorkspaceMaintenanceBanner';

/**
 * True when this build is production and the app is not running on the beta hostname.
 */
function shouldEvaluateProdMaintenanceBanner(): boolean {
  if (!import.meta.env.PROD) {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  return !window.location.hostname.startsWith('beta');
}

/**
 * Persistent notice during production maintenance, shown only for signed-in users
 * who have an active workspace. Toggle via {@link SHOW_PROD_WORKSPACE_MAINTENANCE_BANNER}.
 */
export function ProdWorkspaceMaintenanceBanner() {
  const { status } = useUserState();
  const { status: workspaceStatus, current: workspace } = useWorkspaces();

  if (!SHOW_PROD_WORKSPACE_MAINTENANCE_BANNER) {
    return null;
  }
  if (!shouldEvaluateProdMaintenanceBanner()) {
    return null;
  }
  if (status !== 'signedIn') {
    return null;
  }
  if (workspaceStatus !== 'loaded' || !workspace) {
    return null;
  }

  return (
    <div className="shrink-0 border-b border-orange-400/25 bg-orange-400/5 px-3 py-2 print:hidden">
      <ClarionCallout
        intent="warning"
        title="System maintenance in progress"
        className="rounded-lg"
        role="status"
        aria-live="polite"
      >
        We are applying significant upgrades. The system is temporarily unavailable and should
        resume shortly.
      </ClarionCallout>
    </div>
  );
}
